import { format, formatISO9075, parse } from "date-fns";

export const APP_TIMEZONE = "Asia/Singapore";

const pad2 = (n: string) => n.padStart(2, "0");

export const splitLiteralDateTime = (value: string): { date: string; time: string } | null => {
  if (!value) return null;

  const isoMatch = value.match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/);
  if (isoMatch) {
    return { date: isoMatch[1], time: isoMatch[2] };
  }

  const dmyMatch = value.match(/^(\d{2})\/(\d{2})\/(\d{4})[ T](\d{2}:\d{2})/);
  if (dmyMatch) {
    return { date: `${dmyMatch[3]}-${dmyMatch[2]}-${dmyMatch[1]}`, time: dmyMatch[4] };
  }

  return null;
};

/**
 * True when the string carries a timezone marker (Z or ±HH:MM/±HHMM offset).
 * Such values are absolute instants that display as Asia/Singapore wall time.
 */
export const hasTimezoneMarker = (value: string): boolean =>
  /[Zz]$|[+-]\d{2}:?\d{2}$/.test(value);

const zonedPartsFormatter = new Intl.DateTimeFormat("en-GB", {
  timeZone: APP_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit",
  hourCycle: "h23",
});

const getZonedParts = (instant: Date): { date: string; time: string } => {
  const parts = zonedPartsFormatter.formatToParts(instant);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return {
    date: `${get("year")}-${get("month")}-${get("day")}`,
    time: `${get("hour")}:${get("minute")}`,
  };
};

/**
 * Parse an API datetime string into display parts.
 * - With a timezone marker (Z / offset): the value is an absolute instant;
 *   normalize a colon-less offset (+HHmm, which Safari cannot parse) to
 *   +HH:mm, then convert the instant to APP_TIMEZONE wall time.
 * - Without a marker: the digits are literal wall time; read them as-is.
 * Returns null for empty, date-only, or unparseable input; callers fall
 * back to showing the raw string.
 */
export const parseToClientParts = (value: string): { date: string; time: string } | null => {
  if (!value) return null;
  if (hasTimezoneMarker(value)) {
    const normalized = value.replace(/([+-]\d{2})(\d{2})$/, "$1:$2");
    const instant = new Date(normalized);
    if (Number.isNaN(instant.getTime())) return null;
    return getZonedParts(instant);
  }
  return splitLiteralDateTime(value);
};

/**
 * Current time (or a given instant) as Asia/Singapore wall clock in
 * datetime-local format WITHOUT a timezone marker — the wire convention
 * for delivery timestamps: the API stores naive values as-is, so what is
 * sent is what the database keeps and what the display shows back.
 */
export const nowToAppLocalDateTime = (instant: Date = new Date()): string => {
  const parts = zonedPartsFormatter.formatToParts(instant);
  const get = (type: string) =>
    parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}T${get("hour")}:${get("minute")}:${get("second")}`;
};

/**
 * Format any API datetime string into the value shape accepted by
 * <input type="datetime-local">: naive "YYYY-MM-DDTHH:mm" (seconds truncated).
 * Marker-bearing strings (booking.date arrives as UTC "...Z" via Carbon's
 * jsonSerialize; serializeDate('c') values arrive as "+08:00") are converted
 * to APP_TIMEZONE wall time; naive strings are kept literal. Compose with
 * nowToAppLocalDateTime() for a Singapore wall-clock prefill. Returns ""
 * for empty or unparseable input.
 */
export const toDatetimeLocal = (value: string | null | undefined): string => {
  if (!value) return "";
  const parts = parseToClientParts(value);
  if (!parts) return "";
  return `${parts.date}T${parts.time}`;
};

/**
 * Wall-clock lexicographic key for ordering API datetimes by their
 * Asia/Singapore wall time (marker-aware via parseToClientParts; naive
 * strings stay literal). "YYYY-MM-DDTHH:mm" sorts correctly with plain
 * < / > string comparison. Returns "" for empty or unparseable input.
 */
export const sortableDateTimeKey = (value: string | null | undefined): string => {
  if (!value) return "";
  const parts = parseToClientParts(value);
  if (!parts) return "";
  return `${parts.date}T${parts.time}`;
};

const abbrevMonthNames = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

/**
 * Format any API datetime string to abbreviated display form
 * "dd Mon yyyy, HH:mm" via marker-aware parseToClientParts
 * (Z/offset → Asia/Singapore wall time; naive → literal). Preserves
 * the BookingCalendar "Printed:" header shape without moment().
 * Returns "" for falsy or unparseable input.
 */
export const formatDisplayDateTimeAbbrev = (
  value: string | null | undefined,
): string => {
  if (!value) return "";
  const parts = parseToClientParts(value);
  if (!parts) return "";
  const [yyyyS, mmS, ddS] = parts.date.split("-");
  const [hh, mi] = parts.time.split(":");
  const m = parseInt(mmS, 10);
  const mon = abbrevMonthNames[m - 1] ?? pad2(mmS);
  return `${pad2(ddS)} ${mon} ${yyyyS}, ${pad2(hh)}:${pad2(mi)}`;
};

/**
 * Format any ISO/SQL/locale date-time string to canonical display format
 * "dd/MM/yyyy HH:mm". Marker-bearing strings are converted to Asia/Singapore
 * wall time; naive strings are treated as literal local.
 * Returns "" for falsy input.
 */
export const formatDisplayDateTime = (value: string | null | undefined): string => {
  if (!value) return "";
  const parts = parseToClientParts(value);
  if (!parts) return value;
  const [yyyy, mm, dd] = parts.date.split("-");
  const [hh, mi] = parts.time.split(":");
  return `${pad2(dd)}/${pad2(mm)}/${yyyy} ${pad2(hh)}:${pad2(mi)}`;
};

export const formatDisplayTime = (value: string | null | undefined): string => {
  if (!value) return "";
  const parts = parseToClientParts(value);
  if (!parts) return value;
  const [hh, mi] = parts.time.split(":");
  return `${hh}:${mi}`;
};

/**
 * Format a JS Date (treated as local) to canonical date-time string.
 * Uses local components; does NOT shift timezones.
 */
export const formatDateToDisplay = (date: Date | null | undefined): string => {
  if (!date) return "";
  const yyyy = String(date.getFullYear());
  const mm = pad2(String(date.getMonth() + 1));
  const dd = pad2(String(date.getDate()));
  const hh = pad2(String(date.getHours()));
  const mi = pad2(String(date.getMinutes()));
  return `${dd}/${mm}/${yyyy} ${hh}:${mi}`;
};

export const formatDateToTime = (date: Date | null | undefined): string => {
  if (!date) return "";
  const hh = pad2(String(date.getHours()));
  const mi = pad2(String(date.getMinutes()));
  return `${hh}:${mi}`;
};

/** Literal 12h variant for time labels (e.g. "11:30 PM - 02:30 AM"). */
export const formatDateToTime12h = (date: Date | null | undefined): string => {
  if (!date) return "";
  let h = date.getHours();
  const mi = pad2(String(date.getMinutes()));
  const ampm = h >= 12 ? "PM" : "AM";
  h = h % 12;
  if (h === 0) h = 12;
  return `${h}:${mi} ${ampm}`;
};

const jsFormat = "yyyy-MM-dd'T'HH:mm";
const sqlFormat = "yyyy-MM-dd HH:mm:ss";

/**
 * Legacy helper for the delivery create path (deliveries/index.tsx).
 * Keeps the browser-TZ round-trip for that resource's wire format;
 * not used by the booking domain.
 */
export const convertJsToSql = (jsString: string) =>
  format(parse(jsString, jsFormat, new Date()), sqlFormat);


export const convertIsoToLocale = (isoString: string | null) => {
  if (!isoString) return undefined;
  return formatDisplayDateTime(isoString);
};

/**
 * A helper function to convert Javascript Date to mySQL Date.
 * E.g. mysql date: 2020-01-31.
 *
 * @param date date to be converted
 * @returns
 */
export const convertJsDateToMySqlDate = (date: Date) =>
  formatISO9075(date, { representation: "date" });

/**
 * Convert ISO date string to "date month year" and "hour minute".
 * Marker-bearing values are converted to Asia/Singapore wall time;
 * naive values are kept literal.
 * @param isoString
 * @returns formatted date and time
 */
export const convertIsoToDateTime = (isoString: string) => {
  const parts = parseToClientParts(isoString);
  if (!parts) {
    return { date: isoString, time: "" };
  }
  const [yyyy, mm, dd] = parts.date.split("-");
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const monthName = monthNames[parseInt(mm, 10) - 1] ?? mm;
  const formattedDate = `${parseInt(dd, 10)} ${monthName} ${yyyy}`;
  return {
    date: formattedDate,
    time: parts.time,
  };
};
